import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import "moment/locale/ru";
import moment from "moment";
import { useActions } from "../../../../../hooks/actions";
import cn from "classnames";
import { useSendMessageToChatMutation } from "../../../../../store/chat/chat.api";
import {
    useSendSupportMessageToChatMutation,
    useSendSupportMessageToChatNotAuthMutation,
} from "../../../../../store/support/support.api";
import { IOffer } from "../../../../../models/ordersModel";
import { TranslationObject } from "../../../../../models/translationModal";
import Icon from "../../../../../components/Icon";
import { useNotification } from "../../../../../hooks/useNotification/useNotification";
import { checkStringForForbiddenWords } from "../../../../../untils/reg";
import {
    IChatSupportMessage,
    ISupportMessage,
    SendSupportMessageToChatResponse,
} from "../../../../../models/chatModels";
import {
    getOrCreateAnonymousUserId,
} from "../../../../../store/support/anonymousSupportChatSession";
import {
    beginSupportChatSend,
    isLatestSupportChatSend,
} from "../../../../../store/support/supportChatSendGeneration";
import { chatMessagesNotAuthSelector, chatMessagesSelector, isSupportChatBannedSelector, isSupportCountMsgMaxSelector } from "../../../../../store/support/support.selectors";

type OpenAiConversationTurn = { role: "user" | "assistant"; content: string };

const buildOpenAiConversationFromNotAuthMessages = (
    messages: ISupportMessage[],
): OpenAiConversationTurn[] => {
    return messages.flatMap((message) => {
        const history: OpenAiConversationTurn[] = [];

        if (message.user_message) {
            history.push({
                role: "user",
                content: message.user_message,
            });
        }

        if (typeof message.bot_response === "string" && message.bot_response !== "") {
            history.push({
                role: "assistant",
                content: message.bot_response,
            });
        }

        return history;
    });
};

const buildOpenAiConversationFromSupportGroups = (
    groups: IChatSupportMessage[],
): OpenAiConversationTurn[] => {
    return groups
        .flatMap((group) => group.messages)
        .flatMap((message) => {
            const content = String(message.text || message.message || "").trim();

            if (!content || message.bot_system) {
                return [];
            }

            return [{
                role: message.is_your ? "user" as const : "assistant" as const,
                content,
            }];
        })
        .slice(-50);
};

interface ChatFormProps {
    role?: string;
    chatId?: string | number;
    status?: string;
    orderId?: string;
    offer?: IOffer | null;
    entities?: TranslationObject;
    isSupport?: boolean;
    isAuth?: boolean;
    price?: number;
}

export const ChatForm: React.FC<ChatFormProps> = ({
    chatId,
    entities,
    isSupport,
    isAuth,
}) => {
    const [chatInputValue, setChatInputValue] = useState<string>("");
    const [showLexiconError, setShowLexiconError] = useState<boolean>(false);
    const [showLexiconRegularError, setShowLexiconRegularError] =
        useState<boolean>(false);
    const {
        addSupportMessage,
        addSupportMessageNotAuth,
        replaceSupportMessage,
        replaceSupportMessageNotAuth,
        removeSupportMessage,
        removeSupportMessageNotAuth,
        setSupportChatBanned,
    } = useActions();
    const supportMessages = useSelector(chatMessagesSelector);
    const chatMessagesNotAuth = useSelector(chatMessagesNotAuthSelector);
    const isSupportCountMsgMax = useSelector(isSupportCountMsgMaxSelector);
    const isSupportChatBanned = useSelector(isSupportChatBannedSelector);
    const [sendMessageToChat] = useSendMessageToChatMutation();
    const [sendSupportMessageToChat] = useSendSupportMessageToChatMutation();
    const [sendSupportMessageToChatNotAuth] = useSendSupportMessageToChatNotAuthMutation();

    const supportOpenAiConversationRef = useRef<OpenAiConversationTurn[]>([]);
    const supportOpenAiConversationHydratedRef = useRef(false);

    const readySendMessage = chatId && chatInputValue && !isSupportChatBanned;
    //const readySendMessage = chatId && chatInputValue && offer?.status !== "decline";
    const readySmNotAuth = !isAuth && chatInputValue && !isSupportCountMsgMax && !isSupportChatBanned;

    const { setNotification } = useNotification();

    const createOptimisticAnonymousSupportMessage = (text: string): ISupportMessage => ({
        id: -Date.now(),
        text,
        code: null,
        is_translatable: false,
        created_at: new Date().toISOString(),
        is_your: true,
        is_system: false,
        admin_id: null,
        bot_response: "",
        message: text,
        user_message: text,
        bot_active: true,
        order_id: null,
        support_chat_id: null,
        viewed: null,
    });

    const createOptimisticSupportMessage = (text: string): ISupportMessage => ({
        id: -Date.now(),
        text,
        code: null,
        is_translatable: false,
        created_at: new Date().toISOString(),
        is_your: true,
        is_system: false,
        admin_id: null,
        bot_response: 0,
        message: text,
        order_id: null,
        support_chat_id: chatId ? Number(chatId) : null,
        viewed: null,
    });

    useLayoutEffect(() => {
        if (!isSupport || supportOpenAiConversationHydratedRef.current) {
            return;
        }

        if (isAuth) {
            if (supportMessages.length === 0) {
                return;
            }

            supportOpenAiConversationRef.current = buildOpenAiConversationFromSupportGroups(supportMessages);
            supportOpenAiConversationHydratedRef.current = true;

            return;
        }

        if (chatMessagesNotAuth.length === 0) {
            return;
        }

        supportOpenAiConversationRef.current = buildOpenAiConversationFromNotAuthMessages(chatMessagesNotAuth);
        supportOpenAiConversationHydratedRef.current = true;
    }, [isSupport, isAuth, supportMessages, chatMessagesNotAuth]);

    const appendSupportBotMessageToChat = (botMessage: ISupportMessage) => {
        const botMessageText = botMessage.message ?? botMessage.text ?? "";

        if (!botMessageText) {
            return;
        }

        const botMessageDate = moment
            .utc(botMessage.created_at)
            .local()
            .locale("ru")
            .format("D MMMM");

        supportOpenAiConversationRef.current.push({
            role: "assistant",
            content: String(botMessageText),
        });

        addSupportMessage({
            date: botMessageDate,
            messages: [{
                ...botMessage,
                is_your: false,
                text: botMessageText,
                message: botMessageText,
            }],
        });
    };

    const handleAuthorizedSupportSendResponse = (
        payload: SendSupportMessageToChatResponse["payload"],
        optimisticMessage: ISupportMessage,
        messageText: string,
        sendGeneration: number,
    ) => {
        if (!payload) {
            if (!isLatestSupportChatSend(sendGeneration)) {
                return;
            }

            removeSupportMessage(optimisticMessage.id);
            setChatInputValue(messageText);
            return;
        }

        if (payload.chat_banned) {
            setSupportChatBanned(true);
        }

        const userMessage = payload.user_message;
        const banMessage = payload.ban_message ?? (payload.chat_banned ? payload.bot_message : null);

        if (payload.bot_message && payload.bot_message !== banMessage) {
            appendSupportBotMessageToChat(payload.bot_message);
        }

        if (banMessage) {
            appendSupportBotMessageToChat(banMessage);
        }

        if (!isLatestSupportChatSend(sendGeneration)) {
            return;
        }

        if (userMessage) {
            const isDuplicateUserMessage = supportMessages
                .flatMap((group) => group.messages)
                .some((message) => message.id === userMessage.id && message.id !== optimisticMessage.id);

            if (isDuplicateUserMessage) {
                removeSupportMessage(optimisticMessage.id);
            } else {
                replaceSupportMessage({
                    tempId: optimisticMessage.id,
                    message: {
                        ...optimisticMessage,
                        ...userMessage,
                        text: userMessage.message ?? optimisticMessage.text,
                        message: userMessage.message ?? optimisticMessage.message,
                        is_your: true,
                        bot_response: 0,
                    },
                });
            }
        } else {
            removeSupportMessage(optimisticMessage.id);
        }
    };

    const handleAnonymousSupportSendResponse = (
        response: ISupportMessage,
        optimisticMessage: ISupportMessage,
        sendGeneration: number,
    ) => {
        const botResponse = typeof response.bot_response === "string" ? response.bot_response : "";

        if (botResponse) {
            supportOpenAiConversationRef.current.push({
                role: "assistant",
                content: botResponse,
            });
        }

        if (!isLatestSupportChatSend(sendGeneration)) {
            return;
        }

        const userMessageText = response.user_message ?? "";

        if (userMessageText) {
            replaceSupportMessageNotAuth({
                tempId: optimisticMessage.id,
                message: {
                    ...optimisticMessage,
                    ...response,
                    text: userMessageText,
                    message: userMessageText,
                    user_message: userMessageText,
                    bot_response: botResponse,
                    is_your: true,
                },
            });
        } else {
            removeSupportMessageNotAuth(optimisticMessage.id);
        }
    };

    const isSupersededSupportChatGeneration = (err: unknown): boolean => {
        const error = err as { status?: number; data?: { code?: string } };

        return error?.status === 409
            && error?.data?.code === "support_chat_generation_superseded";
    };

    const getRequestErrorDescription = (err: unknown): string => {
        const error = err as {
            data?: {
                description?: string;
                message?: string;
                payload?: { message?: string };
            };
            message?: string;
        };

        return error?.data?.description
            || error?.data?.message
            || error?.data?.payload?.message
            || error?.message
            || entities?.failed_to_send_message?.value
            || "";
    };

    // Отправшик сообщений
    const senderMessage = (requestData, sender) => {
        sender(requestData)
            .unwrap()
            .then(() => setChatInputValue(""))
            .catch((err) => {
                if (err?.status === 422) {
                    return setShowLexiconError(true);
                }
                setNotification({
                    type: "warn",
                    description: getRequestErrorDescription(err),
                });
            });
        setChatInputValue("");
    }

    // Метод для не авторизованного пользователя
    const handleSendNotAuth = () => {
        if (isAuth) {
            if (!readySendMessage) return;
            const messageText = chatInputValue;
            const history = [...supportOpenAiConversationRef.current];
            supportOpenAiConversationRef.current.push({
                role: "user",
                content: messageText,
            });
            const optimisticMessage = createOptimisticSupportMessage(messageText);
            const optimisticDate = moment
                .utc(optimisticMessage.created_at)
                .local()
                .locale("ru")
                .format("D MMMM");

            addSupportMessage({
                date: optimisticDate,
                messages: [optimisticMessage],
            });
            setChatInputValue("");

            const sendGeneration = beginSupportChatSend();

            sendSupportMessageToChat({
                chatId,
                body: {
                    text: messageText,
                    history,
                },
            })
                .unwrap()
                .then((payload) => {
                    handleAuthorizedSupportSendResponse(
                        payload,
                        optimisticMessage,
                        messageText,
                        sendGeneration,
                    );
                })
                .catch((err) => {
                    if (isSupersededSupportChatGeneration(err)) {
                        return;
                    }

                    supportOpenAiConversationRef.current = history;
                    removeSupportMessage(optimisticMessage.id);

                    if (err?.status === 422) {
                        setShowLexiconError(true);
                        setChatInputValue(messageText);
                        return;
                    }

                    setNotification({
                        type: "warn",
                        description: getRequestErrorDescription(err),
                    });
                    setChatInputValue(messageText);
                });
        } else {
            if (isSupportCountMsgMax || !readySmNotAuth) return;

            const messageText = chatInputValue;
            const anonymousUserId = getOrCreateAnonymousUserId();
            const history = [...supportOpenAiConversationRef.current];
            supportOpenAiConversationRef.current.push({
                role: "user",
                content: messageText,
            });
            const optimisticMessage = createOptimisticAnonymousSupportMessage(messageText);

            addSupportMessageNotAuth(optimisticMessage);
            setChatInputValue("");

            const sendGeneration = beginSupportChatSend();

            sendSupportMessageToChatNotAuth({
                body: {
                    anonymous: anonymousUserId,
                    text: messageText,
                    history,
                },
            })
                .unwrap()
                .then((message) => {
                    handleAnonymousSupportSendResponse(
                        message,
                        optimisticMessage,
                        sendGeneration,
                    );
                })
                .catch((err) => {
                    if (isSupersededSupportChatGeneration(err)) {
                        return;
                    }

                    supportOpenAiConversationRef.current = history;
                    removeSupportMessageNotAuth(optimisticMessage.id);

                    if (err?.status === 422) {
                        setShowLexiconError(true);
                        setChatInputValue(messageText);
                        return;
                    }

                    setNotification({
                        type: "warn",
                        description: getRequestErrorDescription(err),
                    });
                    setChatInputValue(messageText);
                });
        }
    };

    // Метод для авторизованного и не поддержка
    const handleSendAuth = () => {
        if (!readySendMessage) return;

        senderMessage({
            chatId,
            body: {
                text: chatInputValue,
            },
        }, sendMessageToChat);
    };

    const handleSendMessage = () => {
        if (isSupport) {
            handleSendNotAuth();
        } else {
            handleSendAuth();
        }
    };

    useEffect(() => {
        if (isSupport) {
            return;
        }

        if (chatInputValue) {
            if (checkStringForForbiddenWords(chatInputValue)) {
                setShowLexiconRegularError(true);
            } else if (showLexiconRegularError) {
                setTimeout(() => {
                    setShowLexiconRegularError(false);
                }, 2000);
            }
        } else if (!chatInputValue && showLexiconRegularError) {
            setShowLexiconRegularError(false);
        }
    }, [chatInputValue, showLexiconRegularError, isSupport]);

    return (
        <>
            <div className="chat__form form">
                {(showLexiconError || showLexiconRegularError) && (
                    <div className="notification-lexicon__error">
                        {entities?.message_violates_terms_of_system?.value}
                    </div>
                )}
                <div className="form__row">
                    <div className="custom-input form__field">
                        <label>
                            <input
                                type="text"
                                id="message"
                                name="message"
                                placeholder={
                                    entities?.chat_write_message_2?.value
                                }
                                value={chatInputValue}
                                onChange={(e) => {
                                    setChatInputValue(e.target.value);
                                    setShowLexiconError(false);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key !== "Enter") return;
                                    e.preventDefault();
                                    handleSendMessage();
                                }}
                            />
                        </label>
                        <button
                            className={cn("btn-send__message", {
                                disabled: isSupport && !isAuth ? !readySmNotAuth : !readySendMessage,
                            })}
                            type="button"
                            onClick={handleSendMessage}
                        >
                            <Icon
                                type={"send-message"}
                                height={44}
                                width={44}
                            />
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};
